import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, CalendarClock, Clock, Loader2, MessageCircle, X, CheckCheck } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";

const POLL_MS = 60_000;

const KIND_META = {
  scheduled_overdue: { icon: AlertTriangle, tone: "text-rose-600 bg-rose-50" },
  scheduled_upcoming: { icon: CalendarClock, tone: "text-blue-600 bg-blue-50" },
  sla: { icon: Clock, tone: "text-amber-600 bg-amber-50" },
  whatsapp_inbound: { icon: MessageCircle, tone: "text-[#1aa851] bg-[#25D366]/10" },
};

const PIPELINE_ROUTE = {
  mbbs: "/admin",
  general: "/admin/leads",
  counseling: "/admin/counselings",
};

function whenLabel(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function NotificationBell() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setItems(data?.items || []);
      setCount(data?.count || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      load().finally(() => setLoading(false));
    }
  }, [open, load]);

  const go = (it) => {
    setOpen(false);
    if (it.kind === "whatsapp_inbound") {
      nav(`/admin/whatsapp?lead=${it.lead_id}`);
      return;
    }
    const route = PIPELINE_ROUTE[it.pipeline] || "/admin/leads";
    nav(`${route}?focus=${it.lead_id}`);
  };

  const dismiss = async (it, e) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== it.id));
    setCount((c) => (it.read ? c : Math.max(0, c - 1)));
    try {
      await api.post(`/admin/notifications/${it.id}/dismiss`, { at: it.at });
    } catch {
      /* silent — will reappear on next poll if it failed */
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setCount(0);
    try {
      await api.post("/admin/notifications/read-all");
    } catch {
      /* silent */
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notification-bell"
          aria-label="Notifications"
          className="fixed right-3 top-3 z-50 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm transition-colors hover:bg-slate-50"
        >
          <Bell className="h-5 w-5 text-[#1877F2]" />
          {count > 0 && (
            <span
              data-testid="notification-count"
              className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[340px] p-0"
        data-testid="notification-panel"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="text-sm font-semibold text-[#0A2540]">Reminders</div>
          {items.length > 0 ? (
            <button
              type="button"
              onClick={markAllRead}
              className="flex items-center gap-1 text-[11px] font-medium text-[#1877F2] hover:text-[#0A2540]"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          ) : (
            <span className="text-[11px] text-slate-400">All caught up</span>
          )}
        </div>
        <ScrollArea className="max-h-[360px]">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-xs text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div
              className="px-4 py-10 text-center text-xs text-slate-400"
              data-testid="notification-empty"
            >
              All caught up — no reminders right now.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((it) => {
                const meta = KIND_META[it.kind] || KIND_META.sla;
                const Icon = meta.icon;
                return (
                  <li key={it.id} className="group relative flex items-stretch">
                    <button
                      type="button"
                      onClick={() => go(it)}
                      data-testid={`notification-item-${it.lead_id}`}
                      className={`flex w-full items-start gap-3 py-3 pl-4 pr-9 text-left transition-colors hover:bg-slate-50 ${it.read ? "" : "bg-blue-50/40"}`}
                    >
                      <span
                        className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${meta.tone}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {!it.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#1877F2]" data-testid={`notification-unread-dot-${it.lead_id}`} />}
                          <div className={`truncate text-sm ${it.read ? "font-medium text-slate-600" : "font-semibold text-[#0A2540]"}`}>
                            {it.title}
                          </div>
                        </div>
                        <div className="truncate text-[11px] text-slate-500">
                          {it.detail}
                        </div>
                        {it.at && (
                          <div className="mt-0.5 text-[10px] text-slate-400">
                            {whenLabel(it.at)}
                          </div>
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => dismiss(it, e)}
                      data-testid={`notification-dismiss-${it.lead_id}`}
                      aria-label="Dismiss"
                      className="absolute right-2 top-2.5 flex h-6 w-6 items-center justify-center rounded-full text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-600 group-hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
